import React, { useRef } from "react";
import { motion } from "framer-motion";

import { ArrowRight, CodeIcon, ArrowLeft, ExternalLink } from "lucide-react";

const ProjectsSection = () => {
  // Refs for the sliders
  const webMobileSliderRef = useRef(null);
  const aiSliderRef = useRef(null);

  // Web & Mobile App Projects
  const webMobileProjects = [
    {
      id: 1,
      title: "WeRide Mobile - Transportation App",
      description:
        "End-to-end transportation app city to city with mobile and web interfaces, featuring real-time tracking and payment processing.",
      image: "",
      video: "/videos/weride-mobile.mov",
      techStack: ["FastAPI", "React Native", "Supabase", "PostgreSQL"],
      link: "https://www.weride.club/",
    },
    {
      id: 2,
      title: "WeRide Web",
      description:
        "Comprehensive web platform for the WeRide transportation app featuring a marketing landing page, driver account management portal, and administrative dashboard for service monitoring and operations.",
      image: "",
      video: "/videos/weride-web.mov",
      techStack: ["FastAPI", "React Js", "PostgreSQL", "Supabase"],
      link: "https://www.weride.club/",
    },
    {
      id: 3,
      title: "AAAGentive",
      description:
        "Specialized AI agency creating intelligent solutions for South African businesses. We develop conversational AI agents and custom automation workflows that reduce operational costs, save time, and enhance customer experiences in the real estate and hospitality sectors.",
      image: "",
      video: "/videos/aaagentive-web.mov",
      techStack: [
        "React Js",
        "TailwindCSS",
        "Python",
        "FastAPI",
        "PostgreSQL",
        "Supabase",
        "LLMs",
        "LangChain",
      ],
      link: "https://www.aaagentive.com/",
    },
    {
      id: 4,
      title: "Africarmarket",
      description:
        "Cross-border automotive marketplace enabling customers in DR Congo to purchase vehicles from South Africa. Custom-built platform with secure payment processing, inventory management, and logistics coordination.",
      image: "",
      video: "/videos/africarmarket.mov",
      techStack: ["Laravel", "MySQL", "AWS S3", "Heroku"],
      link: "",
    },
  ];

  // AI & Automation Projects
  const aiProjects = [
    {
      id: 1,
      title: "ClientMinder AI",
      description:
        "Automated client communication system that monitors project progress, sends personalized updates, and identifies when clients need attention based on communication patterns.",
      image: "/images/projects/client-minder.jpg",
      techStack: ["LangChain", "n8n", "FastAPI", "OpenAI"],
      link: "",
    },
    {
      id: 2,
      title: "LinkedInGenie",
      description:
        "Automated content generator that transforms your Notion docs, project details, and personal insights into engaging LinkedIn posts optimized for maximum engagement in tech communities.",
      image: "/images/projects/linkedin-genie.jpg",
      techStack: ["OpenAI", "n8n", "Notion API", "Make"],
      link: "",
    },
    {
      id: 3,
      title: "ProposalCrafter",
      description:
        "Intelligent proposal generation system that analyzes client requirements and automatically creates customized project proposals with accurate scope and pricing estimates.",
      image: "/images/projects/proposal-crafter.jpg",
      techStack: ["GPT-4", "Make", "Airtable", "FastAPI"],
      link: "",
    },
    {
      id: 4,
      title: "InvoiceBot",
      description:
        "Automated invoicing and payment tracking system that monitors project milestones, generates invoices, and follows up on payments with personalized reminders.",
      image: "/images/projects/invoice-bot.jpg",
      techStack: ["n8n", "Stripe API", "MongoDB", "LangChain"],
    },
    {
      id: 5,
      title: "LeadQualifier",
      description:
        "AI-powered lead qualification system that analyzes inbound inquiries, qualifies prospects based on your ideal client profile, and prioritizes high-value opportunities.",
      image: "/images/projects/lead-qualifier.jpg",
      techStack: ["Make", "GPT-4", "FastAPI", "Postgres"],
      link: "",
    },
    {
      id: 6,
      title: "ClientVisionBoard",
      description:
        "Collaborative design system that transforms client requirements into visual mood boards, wireframes, and design concepts using AI, streamlining the early project visualization phase.",
      image: "/images/projects/client-vision.jpg",
      techStack: ["DALL-E 3", "FastAPI", "React", "Figma API"],
    },
  ];
  // Function to scroll sliders
  const scrollSlider = (sliderRef, direction) => {
    if (sliderRef.current) {
      const scrollAmount = direction === "left" ? -350 : 350;
      sliderRef.current.scrollBy({ left: scrollAmount, behavior: "smooth" });
    }
  };

  // Project Card Component
  const ProjectCard = ({ project }) => (
    <div className="min-w-[300px] max-w-[300px] bg-white rounded-xl overflow-hidden shadow-md hover:shadow-lg transition-shadow duration-300 flex flex-col h-full border border-gray-100">
      <div className="h-40 overflow-hidden bg-gray-100">
        {/* <img
          src={project.image}
          alt={project.title}
          className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
          onError={(e) => {
            e.target.onerror = null;
            e.target.src =
              "https://web-static.wrike.com/cdn-cgi/image/width=900,format=auto/blog/content/uploads/2024/04/ai.png?av=e89537496d6d8a5787b9785ce8fde1f5";
          }}
        /> */}

        {project.video && (
          <video
            src={project.video}
            autoPlay
            loop
            muted
            playsInline
            className="pointer-events-none mx-auto h-40 w-full object-cover object-top" // needed because random black line at bottom of video
          />
        )}
        {project.image && (
          <img
            src={project.image}
            alt={project.title}
            className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src =
                "https://web-static.wrike.com/cdn-cgi/image/width=900,format=auto/blog/content/uploads/2024/04/ai.png?av=e89537496d6d8a5787b9785ce8fde1f5";
            }}
          />
          //   <Image
          //     src={project.image}
          //     alt={project.title}
          //     width={500}
          //     height={300}
          //     className="h-40 w-full overflow-hidden object-cover object-top"
          //   />
        )}
      </div>

      <div className="p-5 flex flex-col flex-1">
        <h3 className="text-lg font-bold mb-2 font-mono">{project.title}</h3>
        <p className="text-gray-600 text-sm mb-4 flex-1">
          {project.description}
        </p>

        <div className="mt-auto">
          <div className="flex flex-wrap gap-2 mb-4">
            {project.techStack.map((tech, index) => (
              <span
                key={index}
                className="text-xs py-1 px-2 bg-blue-50 text-blue-600 rounded-full"
              >
                {tech}
              </span>
            ))}
          </div>

          {project.link ? (
            <a
              href={project.link}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center text-sm font-medium text-blue-600 hover:text-blue-500 transition-colors"
            >
              View Project
              <ExternalLink className="w-3.5 h-3.5 ml-2" />
            </a>
          ) : (
            <span className="inline-flex items-center text-sm font-medium text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">
              <CodeIcon className="w-3.5 h-3.5 mr-1" />
              In Development
            </span>
          )}
        </div>
      </div>
    </div>
  );

  // Slider Component
  const ProjectSlider = ({ projects, sliderRef, title, subtitle }) => (
    <div className="mb-16">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h3 className="text-2xl font-bold font-mono">{title}</h3>
          <p className="text-gray-600 text-sm">{subtitle}</p>
        </div>

        <div className="flex gap-2">
          <button
            onClick={() => scrollSlider(sliderRef, "left")}
            className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
            aria-label="Scroll left"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <button
            onClick={() => scrollSlider(sliderRef, "right")}
            className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
            aria-label="Scroll right"
          >
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </div>

      <div
        className="flex overflow-x-auto gap-6 pb-4 hide-scrollbar snap-x snap-mandatory"
        ref={sliderRef}
      >
        {projects.map((project) => (
          <div key={project.id} className="snap-start flex-shrink-0">
            <ProjectCard project={project} />
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <section id="projects" className="py-24 mt-16 mb-16 bg-white rounded-2xl">
      <div className="w-full max-w-6xl mx-auto px-6">
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-2 font-mono">
              Featured Projects
            </h2>
            <div className="w-16 h-1 bg-blue-600 mx-auto mb-6"></div>
            <p className="max-w-2xl mx-auto text-gray-600">
              A showcase of my recent work demonstrating expertise in web
              development, mobile applications, AI solutions, and automation.
            </p>
          </motion.div>
        </div>

        {/* Web & Mobile Apps Slider */}
        <ProjectSlider
          projects={webMobileProjects}
          sliderRef={webMobileSliderRef}
          title="Web & Mobile Applications"
          subtitle="Responsive web applications and mobile experiences"
        />

        {/* AI & Automation Slider */}
        <ProjectSlider
          projects={aiProjects}
          sliderRef={aiSliderRef}
          title="AI & Automation Solutions"
          subtitle="Intelligent assistants and workflow automation"
        />

        {/* View All Projects Button */}
        <div className="text-center mt-8">
          <a
            href="#"
            className="inline-flex items-center px-6 py-3 rounded-lg font-medium text-lg border border-black transition-colors group relative overflow-hidden"
          >
            <span className="absolute inset-0 w-0 bg-black transition-all duration-300 ease-out group-hover:w-full"></span>
            <span className="relative z-10 group-hover:text-white">
              View All Projects
            </span>
            <ArrowRight className="ml-2 w-5 h-5 relative z-10 group-hover:text-white" />
          </a>
        </div>
      </div>

      {/* Custom styling for hiding scrollbar but allowing scroll functionality */}
      <style jsx global>{`
        .hide-scrollbar {
          -ms-overflow-style: none; /* Internet Explorer 10+ */
          scrollbar-width: none; /* Firefox */
        }
        .hide-scrollbar::-webkit-scrollbar {
          display: none; /* Safari and Chrome */
        }
      `}</style>
    </section>
  );
};

export default ProjectsSection;
